import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import LoginForm from './components/LoginForm';
import FeatureSlideshow from './components/FeatureSlideshow';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';

const LoginPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/user-account-dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLoginSuccess = (userData) => {
    setIsLoading(false);
    // Navigation is handled in LoginForm component
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-gentle">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center p-2">
            <AppImage 
              src="/assets/images/stillflow_primary_white-1767144960179.svg" 
              alt="StillFlow Logo"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("/assets/images/c22a2fdf-5d74-4a81-9934-01f417fff487_121132386-1766899692964.png")'
        }}
      >
        {/* Lighter overlay for better image visibility */}
        <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" />
      </div>
      
      {/* Two-Panel Layout: Login Form + Feature Slideshow */}
      <div className="relative min-h-screen flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            
            {/* LEFT PANEL: Login Form */}
            <div className="w-full">
              {/* Main Login Card with Glassmorphism Effect */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 animate-fade-in">
                {/* Header Section */}
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-100 h-30 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30 shadow-lg p-4">
                      <AppImage 
                        src="/assets/images/stillflow_primary_white-1767144960179.svg" 
                        alt="StillFlow Logo"
                        className="w-full h-full object-contain drop-shadow-lg"
                      />
                    </div>
                  </div>
                  <h1 className="font-heading text-3xl font-bold text-white mb-2 drop-shadow-lg">
                    Welcome Back
                  </h1>
                  <p className="font-body text-white/90 text-sm drop-shadow">
                    Sign in to access your planner dashboard
                  </p>
                </div>

                {/* Login Form Section */}
                <div className="mt-8">
                  <LoginForm 
                    onSubmit={handleLoginSuccess}
                    loading={isLoading}
                    transparent={true}
                  />
                </div>

                {/* Sign Up Section */}
                <div className="mt-6">
                  <div className="text-center">
                    <p className="font-body text-sm text-white/90 mb-3 drop-shadow">
                      Don't have an account?
                    </p>
                    <button
                      onClick={() => navigate('/register')}
                      className="w-full py-3 px-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-lg font-body text-sm font-semibold text-white transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <Icon name="UserPlus" size={18} />
                      <span>Create New Account</span>
                    </button>
                  </div>
                </div>

                {/* See Pricing Button */}
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/showcase')}
                    className="w-full py-3 px-4 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 rounded-lg font-body text-sm font-bold text-white transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <Icon name="DollarSign" size={18} />
                    <span>See Pricing</span>
                  </button>
                </div>

                {/* Footer Links */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
                    <button
                      onClick={() => navigate('/')}
                      className="font-body text-sm text-white/90 hover:text-white transition-colors flex items-center space-x-1 drop-shadow"
                    >
                      <Icon name="ArrowLeft" size={14} />
                      <span>Back to Home</span>
                    </button>
                    
                    <div className="flex items-center space-x-4 text-xs text-white/80">
                      <button 
                        onClick={() => navigate('/privacy-policy')}
                        className="hover:text-white transition-colors drop-shadow"
                      >
                        Privacy Policy
                      </button>
                      <span>•</span>
                      <button className="hover:text-white transition-colors drop-shadow">
                        Terms of Service
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Help */}
              <div className="mt-6 text-center">
                <p className="font-caption text-xs text-white/90 drop-shadow">
                  Need help? Contact our support team at{' '}
                  <a 
                    href="mailto:support@planistry.com" 
                    className="text-white font-semibold hover:text-white/80 transition-colors"
                  >
                    support@planistry.com
                  </a>
                </p>
              </div>
            </div>

            {/* RIGHT PANEL: Feature Slideshow */}
            <div className="w-full h-full hidden lg:block">
              <div className="sticky top-8">
                <FeatureSlideshow />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg p-6 shadow-2xl animate-scale-in">
            <div className="flex items-center space-x-3">
              <div className="animate-spin">
                <Icon name="Loader2" size={20} className="text-white" />
              </div>
              <span className="font-body text-sm text-white">
                Signing you in...
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;